/**
 * What listening looks like.
 *
 * Voice search had exactly one visual state: the button's label changed from "🎤 Sesle ara"
 * to "● Dinliyor". Everything else — is it hearing me, did it hear me right, is it
 * searching now — was invisible, so a recogniser that mishears is indistinguishable from
 * one that is broken, and the only recovery either way is to try again and hope.
 *
 * This is the missing half: one element that names the step the voice search is on.
 *
 *   listening   a pulsing ring, and the words as they are recognised
 *   heard       what it settled on, held for a beat before the query runs
 *   error       what went wrong, in a sentence
 *
 * ## Why there is no waveform
 *
 * The plan proposed a Canvas 2D waveform driven by an `AnalyserNode`. Building it means
 * opening a *second* microphone capture with `getUserMedia`, because `SpeechRecognition`
 * exposes no audio and no amplitude — it hands back words, nothing else. That is a second
 * live capture on top of a recogniser already holding the microphone, opened purely for
 * decoration, and on some engines the two contend.
 *
 * It would also only ever work on one of the two tiers that can listen at all: the panel's
 * `webapis.voicecontrol` does its recognition internally and never gives this app a stream,
 * so the television — the device where the polish was most wanted — would fall back to
 * something else regardless. The fallback has to exist either way; a desktop-only second
 * capture on top of it is cost without cover.
 *
 * The pulse below is that fallback, promoted. And on the tier that *can* stream words back,
 * the interim transcript is a stronger signal than any waveform: it does not say "I hear
 * something", it says what it heard, which is the thing that actually goes wrong.
 *
 * ## Motion
 *
 * The ring is CSS, on a non-focusable element, which is what makes it legal on the
 * television: `decideMotion` bans JS motion on anything the remote can land on, and this
 * is decoration beside the microphone button rather than the button itself. It respects
 * `prefers-reduced-motion` through the same token block as everything else.
 */
import { Icon } from './Icon';

export type VoiceStep = 'idle' | 'listening' | 'heard' | 'error';

export interface VoiceIndicatorProps {
  step: VoiceStep;
  /** Words so far, on the tier that streams them. Empty on the panel, which sends one result. */
  interim?: string;
  /** What it settled on, shown for `heard`. */
  heard?: string;
  message?: string | null;
}

export function VoiceIndicator({ step, interim = '', heard = '', message }: VoiceIndicatorProps) {
  if (step === 'idle') return null;

  if (step === 'error') {
    return (
      <p className="voice voice--error" role="alert">
        <Icon name="mic" size={1} />
        {message ?? 'Ses tanıma başarısız oldu'}
      </p>
    );
  }

  if (step === 'heard') {
    return (
      <p className="voice voice--heard" role="status">
        <Icon name="check" size={1} />
        <span className="voice__text">{heard}</span>
      </p>
    );
  }

  return (
    /*
     * `role="status"` and not `alert`: this is progress, not a problem. The live region
     * carries the interim text, so somebody who cannot see the ring still hears what is
     * being recognised — which is the whole point of showing it.
     */
    <p className="voice voice--listening" role="status">
      <span className="voice__pulse" aria-hidden />
      <span className="voice__text">{interim || 'Dinliyor…'}</span>
    </p>
  );
}
